
export interface GeoArticleData {
    title: string;
    summary: string;
    authorName: string;
    authorJobTitle?: string;
    authorBio?: string;
    authorSocialLinks?: string[]; // Array of URLs
    publishedAt?: string; // ISO string
    updatedAt?: string; // ISO string
    canonicalUrl?: string;
    imageUrl?: string;
    // Advanced GEO
    publisherName?: string;
    publisherLogo?: string;
    citations?: string[];
    mentions?: Array<{ name: string; type?: string }>;
}

export const geoUtils = {
    /**
     * Generates a JSON-LD script tag for the Article
     */
    generateArticleSchema(data: GeoArticleData): string {
        const schema: any = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": data.title,
            "description": data.summary,
            "image": data.imageUrl,
            "datePublished": data.publishedAt || new Date().toISOString(),
            "dateModified": data.updatedAt || new Date().toISOString(),
            "author": {
                "@type": "Person",
                "name": data.authorName,
                "jobTitle": data.authorJobTitle,
                "description": data.authorBio,
                "sameAs": data.authorSocialLinks
            },
            "publisher": {
                "@type": "Organization",
                "name": data.publisherName || "Content Growth",
                "logo": {
                    "@type": "ImageObject",
                    "url": data.publisherLogo || "https://www.content-growth.com/logo.svg"
                }
            },
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": data.canonicalUrl
            }
        };

        // Add optional advanced fields
        if (data.citations && data.citations.length > 0) {
            schema.citation = data.citations;
        }

        if (data.mentions && data.mentions.length > 0) {
            schema.mentions = data.mentions.map(m => ({
                "@type": m.type || "Thing", // Default to Thing if unknown, or Organization/Person if specified
                "name": m.name
            }));
        }

        // Add speakable property if summary exists
        if (data.summary) {
            // @ts-ignore - Speakable is a valid property but might not be in generic types
            schema.speakable = {
                "@type": "SpeakableSpecification",
                "cssSelector": ["#ai-summary-box"]
            };
        }

        return `<script type="application/ld+json">\n${JSON.stringify(schema, null, 2)}\n</script>`;
    },

    /**
     * Generates a visible "AI Summary" block HTML
     */
    generateVisibleSummary(summary: string): string {
        return `
<div id="ai-summary-box" style="background-color: #f0f4f8; border-left: 4px solid #2563eb; padding: 16px; margin-bottom: 24px; border-radius: 4px;">
  <p style="margin: 0 0 8px 0; font-weight: 600; color: #1e40af; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">
    📌 AI Summary
  </p>
  <p style="margin: 0; color: #1e3a8a; font-size: 15px; line-height: 1.6;">
    ${summary}
  </p>
</div>
`;
    },

    /**
     * Generates a visible "AI Summary" block in Markdown (for Medium etc)
     */
    generateMarkdownSummary(summary: string): string {
        return `> **📌 AI SUMMARY**\n>\n> ${summary}\n\n---\n\n`;
    }
};
